import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {hasInlineUpgradeSurface, onUpgradePrompt, onUpgradePromptDismiss, UpgradePromptEvent} from '../support/upgradePrompt';
import {UPGRADE_URL} from '../support/upgradeUrl';
import {CreditCardIcon, XIcon} from './icons';

/**
 * Ненавязчивый глобальный промпт апгрейда тарифа (CTHS-21, AC2). Подписан на события из
 * axios-интерцептора: при ответе 402 (исчерпана квота/лимит) или 403-premium показывает
 * dismissible-баннер с понятным объяснением и путём апгрейда. CTA ведёт на внутреннюю страницу
 * подписки ({@link UPGRADE_URL}) — единый адрес апгрейда для всего приложения; на внешний портал
 * ведёт уже она (QA R4/R17).
 *
 * Паттерн styleguide «Notification»: {@code role=alert}, slide-from-top, не перехватывает фокус.
 * Монтируется один раз в дереве приложения.
 *
 * QA-6 (2026-09-15). Баннер жил поверх всех экранов и на 390 px уезжал за правый край:
 * центрирование `left-1/2 -translate-x-1/2` затирала keyframe-анимация `fade-in`
 * (она тоже пишет `transform`). Теперь центрируем через `inset-x-4 mx-auto` (см.
 * styleguide §Motion), баннер сам закрывается через {@link AUTO_DISMISS_MS} с паузой по
 * hover/focus и уходит при смене маршрута — ограничение раздела не переезжает на другие
 * страницы. Слой — как у тостов (z 1400).
 */
const AUTO_DISMISS_MS = 8000;

interface UpgradePromptProps {
    /** Время жизни баннера; вынесено в проп ради тестов. */
    autoDismissMs?: number;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({autoDismissMs = AUTO_DISMISS_MS}) => {
    const [event, setEvent] = useState<UpgradePromptEvent | null>(null);
    /** Пауза автозакрытия, пока курсор или фокус на баннере — пользователь читает. */
    const [paused, setPaused] = useState(false);
    const {pathname} = useLocation();
    const pathnameRef = useRef(pathname);
    /** Маршрут, на котором баннер показан: при уходе с него баннер закрывается. */
    const shownAtRef = useRef<string | null>(null);

    pathnameRef.current = pathname;

    useEffect(() => onUpgradePrompt((e) => {
        // Экран, который объясняет гейт сам, баннера не получает: одно сообщение на один отказ.
        if (hasInlineUpgradeSurface()) return;
        shownAtRef.current = pathnameRef.current;
        setPaused(false);
        setEvent(e);
    }), []);

    // Inline-панель могла смонтироваться уже после ответа 403 — тогда она снимает баннер.
    useEffect(() => onUpgradePromptDismiss(() => {
        shownAtRef.current = null;
        setEvent(null);
    }), []);

    useEffect(() => {
        if (shownAtRef.current !== null && shownAtRef.current !== pathname) {
            shownAtRef.current = null;
            setEvent(null);
        }
    }, [pathname]);

    useEffect(() => {
        if (!event || paused) return;
        const timer = setTimeout(() => setEvent(null), autoDismissMs);
        return () => clearTimeout(timer);
    }, [event, paused, autoDismissMs]);

    const dismiss = useCallback(() => setEvent(null), []);

    if (!event) {
        return null;
    }

    return (
        <div
            role="alert"
            data-testid="upgrade-prompt"
            onPointerEnter={() => setPaused(true)}
            onPointerLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
            /* Q19: под шапкой (h-16), иначе баннер перекрывал колокольчик и переключатель темы. */
            className="fixed top-[4.5rem] inset-x-4 mx-auto z-[1400] max-w-md
                       rounded-2xl border border-brand-200 dark:border-brand-800
                       bg-white dark:bg-ink-800 shadow-card-hover p-4 animate-fade-in"
        >
            <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-xl bg-brand-50 dark:bg-brand-900/40
                                flex items-center justify-center text-brand-600 dark:text-brand-400">
                    <CreditCardIcon className="w-5 h-5"/>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 break-words">
                        {event.title}
                    </div>
                    {event.description && (
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 break-words">
                            {event.description}
                        </p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                        <Link
                            to={UPGRADE_URL}
                            onClick={() => setEvent(null)}
                            data-testid="upgrade-prompt-cta"
                            className="inline-flex items-center justify-center min-h-[44px] px-4
                                       rounded-xl bg-brand-600 text-white text-sm font-medium
                                       hover:bg-brand-700 transition-colors"
                        >
                            Обновить тариф
                        </Link>
                    </div>
                </div>
                <button
                    type="button"
                    aria-label="Закрыть"
                    data-testid="upgrade-prompt-close"
                    onClick={dismiss}
                    className="shrink-0 -mr-1 -mt-1 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center
                               rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                >
                    <XIcon className="w-5 h-5"/>
                </button>
            </div>
        </div>
    );
};

export default UpgradePrompt;
